import { AuthUser, getCurrentUser, signOut } from 'aws-amplify/auth'
import * as React from 'react'

import API from '@/api'
import { getLoggedUser, logoutUser } from '@/api/auth'
import { is400Error, NetworkError } from '@/error'
import type { SocialProvider } from '@/types/social-provider'
import type { User } from '@/types/user'
import { getToken } from '@/utils/auth'
import { hasText } from '@/utils/helpers'
import history from '@/utils/history'

type Props = {
  children: React.ReactNode
}

type AuthContextType = {
  isAuthenticated: boolean | null
  loading: boolean
  loginWithRedirect: (value: { targetUrl: string | null }) => undefined
  logout: (value: { returnTo: string; query?: string }) => Promise<undefined>
  paymentWithRedirect: (value: { targetUrl: string | null }) => undefined
  redirectPath: string | null | undefined
  setRedirectPath: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >
  authIsReady: boolean
  socialProviders: Array<SocialProvider>
  user: User | null
  handleRedirectCallback: () => Promise<undefined>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  clearRedirectPath: () => undefined
  getTokenSilently: () => Promise<string | null | undefined>
  getProviderUser: () => Promise<AuthUser | null>
}

export const AuthContext = React.createContext<AuthContextType>({
  authIsReady: false,
  clearRedirectPath: () => undefined,
  getTokenSilently: async () => Promise.resolve(''),
  handleRedirectCallback: async () => Promise.resolve(undefined),
  isAuthenticated: false,
  loading: false,
  loginWithRedirect: () => undefined,
  logout: async () => Promise.resolve(undefined),
  paymentWithRedirect: () => undefined,
  redirectPath: null,
  setLoading: () => undefined,
  setRedirectPath: () => undefined,
  socialProviders: [],
  user: null,
  getProviderUser: async () => Promise.resolve(null),
})

AuthContext.displayName = 'AuthContext'

export function AuthProvider({ children }: Props): React.ReactNode {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(
    null
  )
  const [redirectPath, setRedirectPath] = React.useState<
    string | null | undefined
  >(null)
  const [user, setUser] = React.useState<User | null>(null)
  const [loading, setLoading] = React.useState<boolean>(true)

  const [authIsReady, setAuthIsReady] = React.useState(false)

  const [socialProviders, setSocialProviders] = React.useState<
    SocialProvider[]
  >([])

  const clearLoggedUserState = React.useCallback(() => {
    setIsAuthenticated(false)
    setUser(null)
  }, [])

  const updateSocialProviders = React.useCallback(async () => {
    try {
      const providers: {
        results: SocialProvider[]
      } = await API.getSocialProviders()
      setSocialProviders(providers?.results || ([] as SocialProvider[]))
    } catch {
      // do nothing
    }
  }, [])

  React.useEffect(() => {
    const initAuth = async () => {
      setLoading(true)
      const storedToken = await getToken()
      const isAuthenticated = Boolean(storedToken)
      setIsAuthenticated(isAuthenticated)
      if (isAuthenticated) {
        try {
          const user = await getLoggedUser()
          setUser(user as User)
        } catch (err) {
          if (err instanceof NetworkError && is400Error(err.status)) {
            clearLoggedUserState()
            setAuthIsReady(true)
          }
        }
      } else {
        await logout()
      }

      await updateSocialProviders()

      setLoading(false)
      setAuthIsReady(true)
    }
    initAuth().catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err)
    })
    // eslint-disable-next-line
  }, [])

  const loginWithRedirect = (
    options: { targetUrl: string | null } | null = { targetUrl: null }
  ): undefined => {
    const { targetUrl } = options || {}

    setRedirectPath(targetUrl)
    history.push('/login')
  }

  const paymentWithRedirect = (
    options: { targetUrl: string | null } | null = { targetUrl: null }
  ): undefined => {
    const { targetUrl } = options || {}

    setRedirectPath(targetUrl)
    history.push('/setup-card')
  }

  const logout = React.useCallback(
    async (
      options: { returnTo: string; query?: string } | null = {
        returnTo: window.location.pathname,
        query: window.location.search,
      }
    ): Promise<undefined> => {
      const { returnTo, query } = options || { returnTo: '', query: '' }

      try {
        await logoutUser()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }

      try {
        await signOut()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }

      clearLoggedUserState()
      if (returnTo !== '/login') {
        history.replace(returnTo + (query || ''))
      }
    },
    [clearLoggedUserState]
  )

  const clearRedirectPath = (): undefined => {
    setRedirectPath(null)
  }

  const handleRedirectCallback = async (): Promise<undefined> => {
    setLoading(true)
    const user = await getLoggedUser()
    setUser(user as User)
    setIsAuthenticated(!!user)
    setAuthIsReady(true)
    setLoading(false)
  }

  // token listener on focus tab instance
  React.useEffect(() => {
    async function onFocus() {
      if (isAuthenticated && !hasText(await getToken())) {
        logout().catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err)
        })
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.addEventListener('focus', onFocus)
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.removeEventListener('focus', onFocus)
    }
  }, [isAuthenticated, logout])

  const getTokenSilently = async () => await getToken()

  const getProviderUser = React.useCallback(async () => {
    const user = await getCurrentUser()
    return user
  }, [])

  return (
    <AuthContext.Provider
      value={{
        authIsReady,
        clearRedirectPath,
        getTokenSilently,
        handleRedirectCallback,
        isAuthenticated,
        loading,
        loginWithRedirect,
        logout,
        paymentWithRedirect,
        redirectPath,
        setLoading,
        setRedirectPath,
        socialProviders,
        user,
        getProviderUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext(): AuthContextType {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within a AuthProvider')
  }
  return context
}
